import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import LoadingScreen from "./LoadingScreen";
import { toast } from "react-toastify";
import stringSimilarity from "string-similarity";
import "./CopyPasteProblem.css";
import CopyPasteFlowAnimation from "./CopyPasteFlowAnimation";
import Footer from "../components/Footer";

const suggestions = [
  "Two Sum",
  "FizzBuzz",
  "Reverse Linked List",
  "Recursive tree traversal",
  "Sliding Window Maximum",
];

const normalizeExpectedOutput = (value, returnType) => {
  try {
    if (returnType === "integer") {
      const parsed = parseInt(value);
      return isNaN(parsed) ? value : parsed;
    }

    if (returnType === "boolean") {
      return String(value).toLowerCase() === "true";
    }

    if (returnType === "string") {
      const inner = String(value).replace(/^['"]|['"]$/g, "");
      return inner;
    }

    if (
      ["string[]", "character[][]", "integer[]", "integer[][]"].includes(
        returnType
      ) ||
      ["ListNode", "TreeNode", "Node", "object"].includes(returnType)
    ) {
      return typeof value === "string" ? JSON.parse(value) : value;
    }

    return value;
  } catch {
    return value;
  }
};

const normalizeInputs = (inputs, inputVars) => {
  return inputs.map((input, index) => {
    const type = inputVars?.[index]?.type;

    try {
      if (type === "string") {
        const inner = String(input).replace(/^['"]|['"]$/g, "");
        return `'${inner}'`;
      }

      if (type === "boolean") {
        return String(input).toLowerCase();
      }

      if (type === "integer" || type === "number") {
        return parseInt(input); // ✅ return number directly
      }

      if (
        type === "string[]" ||
        type === "character[][]" ||
        type === "integer[]" ||
        type === "integer[][]"
      ) {
        const parsed = Array.isArray(input) ? input : JSON.parse(input);
        return JSON.stringify(parsed);
      }

      if (type === "ListNode" || type === "TreeNode" || type === "Node") {
        const parsed = Array.isArray(input) ? input : JSON.parse(input);
        return JSON.stringify(parsed);
      }

      if (type === "object") {
        return JSON.stringify(
          typeof input === "string" ? JSON.parse(input) : input
        );
      }

      return input;
    } catch {
      return input;
    }
  });
};

const normalizeExpectedOutputForStorage = (value, returnType) => {
  try {
    if (["string", "integer", "boolean"].includes(returnType)) {
      return String(value);
    }
    if (
      [
        "string[]",
        "character[][]",
        "integer[]",
        "integer[][]",
        "ListNode",
        "TreeNode",
        "Node",
        "object",
      ].includes(returnType)
    ) {
      return JSON.stringify(
        typeof value === "string" ? JSON.parse(value) : value
      );
    }
    return String(value);
  } catch {
    return String(value);
  }
};

const CopyPasteProblem = () => {
  const [userInput, setUserInput] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("javascript");
  const [placeholderSuggestion, setPlaceholderSuggestion] = useState("");
  const [importsUsed, setImportsUsed] = useState(null);
  const [importLimit, setImportLimit] = useState(null);
  const [generationCompleted, setGenerationCompleted] = useState(false);
  const [loadingStageIndex, setLoadingStageIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const init = async () => {
      // Set a random placeholder suggestion
      const random =
        suggestions[Math.floor(Math.random() * suggestions.length)];
      setPlaceholderSuggestion(random);

      // Fetch import usage info
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;

      try {
        const res = await axios.get(`${baseUrl}/users/account/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const user = res.data;
        const role = user.role;

        const limits = {
          FREE_USER: 0,
          PAID_USER: 1,
          PREMIUM_USER: 10,
          ADMIN: Infinity,
        };

        const limit = limits[role];
        const used = user.customImportsThisMonth ?? 0;

        setImportLimit(limit);
        setImportsUsed(used);
      } catch (err) {
        console.error("Failed to fetch user import info:", err);
      }
    };

    init();
  }, []);

  const handleUserInputChange = (e) => setUserInput(e.target.value);
  const handleLanguageChange = (e) => setSelectedLanguage(e.target.value);

  const handleGenerateBlocksFromInput = async (
    customInput,
    existingToastId = null
  ) => {
    const inputToUse = customInput || userInput;
    const toastId =
      existingToastId ||
      toast.loading("Creating your problem... This may take a minute.", {
        position: "bottom-right",
        autoClose: false,
        draggable: false,
      });

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    setLoadingStageIndex(0);

    if (!userId || !token) {
      toast.update(toastId, {
        render: "User is not logged in. Please log in to add a problem.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      return;
    }

    setLoading(true);
    setError(null);

    const generateSolutionsAndAddProblem = async (rewrittenProblem) => {
      try {
        toast.update(toastId, {
          render: "Generating full solution set...",
          type: "info",
          isLoading: true,
          autoClose: false,
        });

        const solutionsResponse = await axios.post(
          `${baseUrl}/problems/generate-solutions`,
          {
            userInput: JSON.stringify(rewrittenProblem),
            language: selectedLanguage,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const rawProblem = solutionsResponse.data;
        rawProblem.userId = userId;

        // 🔧 Normalize test case outputs
        rawProblem.testCases = rawProblem.testCases.map((tc) => ({
          ...tc,
          expectedOutput: normalizeExpectedOutput(
            tc.expectedOutput,
            rawProblem.returnType
          ),
        }));

        // 🔧 Normalize test case outputs
        rawProblem.testCases = rawProblem.testCases.map((tc) => ({
          ...tc,
          expectedOutput: normalizeExpectedOutput(
            tc.expectedOutput,
            rawProblem.returnType
          ),
          inputs: normalizeInputs(tc.inputs, rawProblem.inputVars),
        }));

        setLoadingStageIndex(2);

        toast.update(toastId, {
          render: "Validating solutions against test cases...",
          isLoading: true,
        });

        const validateResponse = await axios.post(
          `${baseUrl}/problems/validate-solutions`,
          {
            problemData: rawProblem,
            language: selectedLanguage,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const validSolutions = validateResponse.data.validSolutions;

        setLoadingStageIndex(3);

        if (!validSolutions || validSolutions.length === 0) {
          toast.update(toastId, {
            render:
              "Original solution failed validation. Suggesting an alternative...",
            type: "warning",
            isLoading: true,
          });

          const titleResponse = await axios.post(
            `${baseUrl}/problems/generate-similar-title`,
            {
              userInput: rewrittenProblem.title || inputToUse,
              language: selectedLanguage,
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const suggestedTitle = titleResponse.data.title;

          if (!suggestedTitle) {
            throw new Error(
              "No valid solutions and failed to generate alternative title."
            );
          }

          toast.update(toastId, {
            render: `Trying again with: \"${suggestedTitle}\"...`,
            type: "info",
            isLoading: true,
            autoClose: false,
          });

          setUserInput(suggestedTitle);
          await new Promise((res) => setTimeout(res, 5000));

          await handleGenerateBlocksFromInput(suggestedTitle, toastId);
          return;
        }

        toast.update(toastId, {
          render: "Processing solutions...",
          isLoading: true,
        });

        const processResponse = await axios.post(
          `${baseUrl}/problems/process-solutions`,
          {
            problemData: {
              ...rawProblem,
              solutions: validSolutions,
            },
            language: selectedLanguage,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const processedProblem = processResponse.data;

        // ✅ Normalize expectedOutputs for STORAGE
        processedProblem.problemData.testCases =
          processedProblem.problemData.testCases.map((tc) => ({
            ...tc,
            expectedOutput: normalizeExpectedOutputForStorage(
              tc.expectedOutput,
              rawProblem.returnType
            ),
          }));

        const addResponse = await axios.post(
          `${baseUrl}/problems/add`,
          processedProblem,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        await axios.post(
          `${baseUrl}/users/increment-custom-import`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const newProblemId = addResponse.data.id;

        setLoadingStageIndex(4);

        toast.update(toastId, {
          render: (
            <div>
              <p>Your custom problem is ready to go!</p>
              <button
                className="toast-button"
                onClick={() => {
                  navigate(`/problems/${newProblemId}`, {
                    state: { selectedLanguage },
                  });
                  toast.dismiss();
                }}
              >
                Solve Problem Now!
              </button>
            </div>
          ),
          type: "success",
          isLoading: false,
          autoClose: false,
        });
      } catch (err) {
        console.error(
          "❌ Error generating, validating, or adding problem:",
          err
        );
        toast.update(toastId, {
          render:
            "Something went wrong during problem creation. Please try again.",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
        setGenerationCompleted(true); // ✅ disable button
      }
    };

    try {
      const rewriteResponse = await axios.post(
        `${baseUrl}/problems/rewrite-problem-json`,
        { userInput: inputToUse, language: selectedLanguage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const generatedProblemData = rewriteResponse.data;
      const validCategories = [
        "ARRAYS",
        "STRINGS",
        "GRAPHS",
        "DYNAMIC_PROGRAMMING",
        "TREES",
        "SEARCH",
        "LINKED_LISTS",
        "STACKS_QUEUES",
        "SORTING",
        "GREEDY",
        "DICTIONARIES_HASHMAPS",
      ];
      const validDifficulties = ["EASY", "MEDIUM", "HARD"];

      generatedProblemData.category = validCategories.includes(
        generatedProblemData.category
      )
        ? generatedProblemData.category
        : "ARRAYS";

      generatedProblemData.difficulty = validDifficulties.includes(
        generatedProblemData.difficulty
      )
        ? generatedProblemData.difficulty
        : "MEDIUM";

      generatedProblemData.input = generatedProblemData.input || " ";
      generatedProblemData.inputVars = generatedProblemData.inputVars || " ";
      generatedProblemData.returnType =
        generatedProblemData.returnType || "Unknown";
      generatedProblemData.testCases = generatedProblemData.testCases || [];
      generatedProblemData.userId = userId;

      if (
        Array.isArray(generatedProblemData.examples) &&
        typeof generatedProblemData.examples[0] === "object"
      ) {
        generatedProblemData.examples = generatedProblemData.examples.map(
          (ex) => `Input: ${ex.input} -> Output: ${ex.output}`
        );
      }

      const existingProblemsRes = await axios.get(
        `${baseUrl}/problems?isAdminAdded=true`
      );
      const existingProblems = existingProblemsRes.data || [];

      const similarityResults = existingProblems.map((problem) => {
        const combinedExisting =
          `${problem.title} ${problem.description}`.toLowerCase();
        const combinedGenerated =
          `${generatedProblemData.title} ${generatedProblemData.description}`.toLowerCase();
        const score = stringSimilarity.compareTwoStrings(
          combinedGenerated,
          combinedExisting
        );
        return { title: problem.title, id: problem.id, score };
      });

      const similarProblems = similarityResults
        .filter((p) => p.score > 0.6)
        .sort((a, b) => b.score - a.score);


      if (similarProblems.length > 0) {
        const match = similarProblems[0];
        // console.log(
        //   `🧠 Similar match found: "${match.title}" (ID: ${match.id}) — Score: ${match.score.toFixed(3)}`
        // );

        toast.update(toastId, {
          render: (
            <div>
              <p>
                You've imported a problem that's <strong>very similar</strong>{" "}
                to one already in our database!
              </p>
              <p style={{ fontWeight: "bold", marginTop: "0.5rem" }}>
                {match.title}
              </p>
              <p
                style={{
                  marginTop: "0.5rem",
                  fontStyle: "italic",
                  color: "#888",
                }}
              >
                ✅ This will <strong>not</strong> count against your monthly
                import limit.
              </p>
              <div
                style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}
              >
                <button
                  className="toast-button"
                  onClick={() => {
                    navigate(`/problems/${match.id}`, {
                      state: { selectedLanguage },
                    });
                    toast.dismiss(toastId);
                  }}
                >
                  Try it Now
                </button>
              </div>
            </div>
          ),
          type: "info",
          isLoading: false,
          autoClose: false,
        });

        setGenerationCompleted(true); // ✅ disable button
        setLoading(false);
        return;
      }

      setLoadingStageIndex(1);
      await generateSolutionsAndAddProblem(generatedProblemData);
    } catch (err) {
      console.error("Error generating problem:", err);
      toast.update(toastId, {
        render:
          "Failed to generate and add the problem. Please try again later.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
      setError("Failed to generate and add the problem");
      setLoading(false);
    }
  };

  return (
    <div className="add-prob">
      <div className="page-header">
        <h1 className="main-title">Generate a Custom Problem</h1>
      </div>

      <div className="form-and-usage-wrapper">
        {/* Left column */}
        <div className="animation-column">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-text">
              Stuck on a problem? <br />
              <strong>Type the name or brief description here</strong>
            </div>
          </div>

          <div className="step-arrow">↓</div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-text">
              Click <strong>"Generate Blocks"</strong>
            </div>
          </div>

          <div className="step-arrow">↓</div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-text">
              Receive an <strong>original BlockCode problem</strong> <br />
              with <strong>custom blocks</strong> ready to solve!
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="form-and-imports-column">
          <div className="form-container">
            <p className="subtitle">
              Type the <strong>name</strong> or a{" "}
              <strong>brief description</strong> of a coding problem you'd like
              to solve:
            </p>
            <input
              className="copy-paste-input"
              type="text"
              value={userInput}
              onChange={handleUserInputChange}
              placeholder={placeholderSuggestion}
            />

            <div className="form-controls">
              <button
                onClick={() => {
                  if (generationCompleted) return; // ✅ Prevent re-click after completion
                  if (importLimit !== null && importsUsed >= importLimit) {
                    navigate("/membership");
                  } else {
                    handleGenerateBlocksFromInput();
                  }
                }}
                className={
                  importLimit !== null && importsUsed >= importLimit
                    ? "import-upgrade-button"
                    : generationCompleted
                      ? "import-disabled-button"
                      : ""
                }
                disabled={generationCompleted}
              >
                {generationCompleted
                  ? "Problem Generated"
                  : importLimit !== null && importsUsed >= importLimit
                    ? "Upgrade for More"
                    : "Generate Blocks"}
              </button>
            </div>

            <p className="disclaimer">
              Problems generated will be <strong>original</strong> BlockCode
              challenges that teach similar techniques to your input.
            </p>

            {loading && (
              <div className="loading-container">
                <LoadingScreen currentStageIndex={loadingStageIndex} />
                <p>Loading... Please wait while we build your problem.</p>
              </div>
            )}

            {error && <p className="error">{error}</p>}
          </div>

          <div className="imports-counter">
            {importLimit !== null && importsUsed !== null && (
              <div className="imports-counter-box">
                <p className="imports-title">Custom Imports</p>
                <p className="imports-used">
                  {importsUsed} / {importLimit === Infinity ? "∞" : importLimit}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <CopyPasteFlowAnimation /> */}

      <Footer />
    </div>
  );
};

export default CopyPasteProblem;
